/**
 * ChatMessage.jsx — Individual Chat Message Component
 *
 * Renders different message types:
 *   - 'text'  : Plain text / markdown-lite (bold, italic, bullet points)
 *   - 'table' : SAP data formatted as a responsive table with summary text
 *   - 'error' : Error messages styled in red
 *
 * Each message knows whether it came from the user or from CAI (assistant).
 */

import React, { memo, useState, useCallback } from 'react';
import { format } from 'date-fns';
import { Bot, User, AlertCircle, Database, Search, X, Download, Copy, Check, BarChart2, TableIcon } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import './ChatMessage.css';

// ── Copy-to-clipboard helper ──────────────────────────────────────────────────
// Returns a hook-friendly function that copies text and briefly shows a tick icon.
// useCopyButton() → { copied, copy }
function useCopyButton() {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for browsers that don't support clipboard API
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity  = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    // Reset tick back to copy icon after 2 seconds
    setTimeout(() => setCopied(false), 2000);
  }, []);

  return { copied, copy };
}

// ── Module colour tags ────────────────────────────────────────────────────────
const MODULE_COLOURS = {
  MM:   'var(--clr-mm)',
  SD:   'var(--clr-sd)',
  FI:   'var(--clr-fi)',
  PP:   'var(--clr-pp)',
  ITSM: 'var(--clr-itsm)',
};

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * @param {object}  message          - Message data object
 * @param {string}  message.id       - Unique ID
 * @param {string}  message.role     - 'user' | 'assistant'
 * @param {string}  message.type     - 'text' | 'table' | 'error'
 * @param {string}  [message.text]   - Text content (for type 'text')
 * @param {string}  [message.summary]- Summary line (for type 'table')
 * @param {object}  [message.tableData] - { headers: [], rows: [[]] }
 * @param {string}  [message.module] - SAP module tag: 'MM' | 'SD' | 'FI' | 'PP'
 * @param {Date}    message.timestamp
 */
const ChatMessage = memo(function ChatMessage({ message, onSuggestionClick }) {
  const isUser = message.role === 'user';

  // Copy button for assistant text responses AND user questions
  const { copied, copy } = useCopyButton();
  const showAssistantCopy = !isUser && message.type === 'text' && message.text;
  const showCopyBtn = showAssistantCopy || isUser;
  const copyText = isUser
    ? (message.text || '')
    : (message.text || '').replace(/\*\*/g, '').replace(/\*/g, '');

  return (
    <div className={`msg-row ${isUser ? 'msg-row--user' : 'msg-row--assistant'}`}>

      {/* Avatar */}
      {!isUser && (
        <div className="msg-avatar msg-avatar--ai" aria-hidden="true">
          <Bot size={16} />
        </div>
      )}

      <div className="msg-bubble-wrapper">
        {/* Module tag (assistant messages only) */}
        {!isUser && message.module && (
          <div className="msg-module-tag"
            style={{ color: MODULE_COLOURS[message.module] || 'var(--clr-accent)' }}>
            <Database size={10} />
            SAP {message.module}
          </div>
        )}

        {/* Bubble */}
        <div className={`msg-bubble ${
          isUser                   ? 'msg-bubble--user'      :
          message.type === 'error' ? 'msg-bubble--error'     :
          'msg-bubble--assistant'
        }`}>
          {renderContent(message)}
        </div>

        {/* Timestamp + copy button row */}
        <div className={`msg-meta ${isUser ? 'msg-meta--right' : ''}`}>
          <div className={`msg-time ${isUser ? 'msg-time--right' : ''}`}>
            {format(message.timestamp, 'HH:mm')}
          </div>
          {/* Copy button for assistant responses and user questions */}
          {showCopyBtn && (
            <button
              className={`msg-copy-btn ${copied ? 'msg-copy-btn--done' : ''}`}
              onClick={() => copy(copyText)}
              aria-label={copied ? 'Copied!' : 'Copy'}
              title={copied ? 'Copied!' : 'Copy to clipboard'}
            >
              {copied ? <Check size={11} /> : <Copy size={11} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>

        {/* Follow-up suggestion chips */}
        {!isUser && message.suggestions?.length > 0 && onSuggestionClick && (
          <div className="msg-suggestions">
            {message.suggestions.map((s, i) => (
              <button key={i} className="msg-suggestion-chip" onClick={() => onSuggestionClick(s)}>
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* User avatar */}
      {isUser && (
        <div className="msg-avatar msg-avatar--user" aria-hidden="true">
          <User size={16} />
        </div>
      )}
    </div>
  );
});

// ─── Content Renderers ────────────────────────────────────────────────────────

function renderContent(message) {
  if (message.type === 'error') {
    return (
      <div className="msg-error-content">
        <AlertCircle size={16} />
        <span>{message.text}</span>
      </div>
    );
  }

  if (message.type === 'table') {
    return (
      <>
        {/* Summary text above table */}
        {message.summary && (
          <div className="msg-summary">
            <FormattedText text={message.summary} />
          </div>
        )}
        {/* Data table — rendered by TableContent which owns search state */}
        {message.tableData && <TableContent tableData={message.tableData} module={message.module} />}
      </>
    );
  }

  // Default: 'text' type
  return <FormattedText text={message.text || ''} />;
}

// ─── Export helpers ───────────────────────────────────────────────────────────

/** Returns a filename like CAI_Export_SD_13Apr2026 */
function buildFilename(sapModule) {
  const now = new Date();
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const dd  = String(now.getDate()).padStart(2, '0');
  const mon = months[now.getMonth()];
  const yy  = now.getFullYear();
  const mod = sapModule || 'SAP';
  return `Querely_Export_${mod}_${dd}${mon}${yy}`;
}

/** Quote a single CSV cell value */
function csvCell(val) {
  return `"${String(val ?? '').replace(/"/g, '""')}"`;
}

function exportToCSV(headers, rows, sapModule) {
  const header = headers.map(csvCell).join(',');
  const body   = rows.map(row => row.map(csvCell).join(',')).join('\n');
  const csv    = `${header}\n${body}`;
  const blob   = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url    = URL.createObjectURL(blob);
  const a      = document.createElement('a');
  a.href       = url;
  a.download   = `${buildFilename(sapModule)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function exportToExcel(headers, rows, sapModule) {
  const data = [headers, ...rows.map(row => row.map(cell => cell ?? ''))];
  const ws   = XLSX.utils.aoa_to_sheet(data);
  const wb   = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Querely Export');
  XLSX.writeFile(wb, `${buildFilename(sapModule)}.xlsx`);
}


// ─── Table with Search ────────────────────────────────────────────────────────
// Separate component so it can hold its own search state.
// Hooks cannot be called inside plain render functions — only in components.

// ── Chart helpers ─────────────────────────────────────────────────────────────

const CHART_ENABLED = false; // parked — re-enable when aggregation logic is solid
const CHART_COLORS = ['#8b5cf6','#3b82f6','#10b981','#f59e0b','#ef4444','#06b6d4','#ec4899','#84cc16'];

function detectChartData(headers, rows) {
  if (rows.length < 2) return null;

  const sample = rows.slice(0, 20);
  const parseNum = v => parseFloat(String(v ?? '').replace(/[₹$,€£,\s]/g, ''));

  // ── Find best VALUE column: numeric, non-sequential, prefer currency/decimal ──
  let valueIdx = -1, valueBestScore = -1;
  for (let c = 0; c < headers.length; c++) {
    const vals = sample.map(r => r[c]);
    const nums  = vals.map(v => parseNum(v));
    const numericCount = nums.filter(n => !isNaN(n) && isFinite(n)).length;
    if (numericCount < sample.length * 0.6) continue;
    // Skip columns where all values are sequential integers (SO Number, Item No)
    const isSequential = nums.filter(n => !isNaN(n)).every((n, i, a) => i === 0 || Math.abs(n - a[i-1]) <= 1);
    if (isSequential) continue;
    const hasCurrency = vals.some(v => /[₹$€£]/.test(String(v ?? '')));
    const hasDecimal  = vals.some(v => /\.\d+/.test(String(v ?? '')));
    const score = (hasCurrency ? 4 : 0) + (hasDecimal ? 2 : 0) + 1;
    if (score > valueBestScore) { valueBestScore = score; valueIdx = c; }
  }
  if (valueIdx === -1) return null;

  // ── Find best LABEL column ──
  // Good label: short text, NOT all same, NOT all unique (primary key), medium cardinality
  let labelIdx = -1, labelBestScore = -1;
  for (let c = 0; c < headers.length; c++) {
    if (c === valueIdx) continue;
    const vals   = sample.map(r => String(r[c] ?? ''));
    if (vals.some(v => v.length > 40)) continue;
    const unique = new Set(vals.filter(v => v)).size;
    if (unique <= 1) continue;                               // all same (e.g. DocType=OR) — skip
    if (unique === sample.length) continue;                  // all unique (e.g. SO Number) — skip
    // Cardinality score: sweet spot 2–10 unique values; penalise if too many
    const cardScore   = unique <= 10 ? unique : Math.max(1, 10 - (unique - 10));
    const allNumeric  = vals.every(v => !isNaN(parseNum(v)) && isFinite(parseNum(v)));
    const score       = cardScore * (allNumeric ? 1 : 3);   // text labels preferred 3×
    if (score > labelBestScore) { labelBestScore = score; labelIdx = c; }
  }
  if (labelIdx === -1) return null;

  // Aggregate: group ALL rows by label, sum values — meaningful even for raw lists
  const agg = new Map();
  for (const row of rows) {
    const label = String(row[labelIdx] ?? '').slice(0, 20) || '(blank)';
    const val   = parseNum(row[valueIdx]) || 0;
    agg.set(label, (agg.get(label) || 0) + val);
  }

  // Sort by value descending, cap at 15 bars
  return [...agg.entries()]
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
    .slice(0, 15)
    .map(([label, value]) => ({ label, value: Math.round(value * 100) / 100 }));
}

function TableContent({ tableData, module: sapModule }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showChart, setShowChart]   = useState(false);
  const [chartType, setChartType]   = useState('bar'); // 'bar' | 'line'

  // L1-7: Copy table as tab-separated text (pastes cleanly into Excel / emails)
  const { copied: tableCopied, copy: copyTable } = useCopyButton();

  const copyTableAsText = useCallback(() => {
    const { headers, rows } = tableData;
    const lines = [headers.join('\t'), ...rows.map((r) => r.join('\t'))];
    copyTable(lines.join('\n'));
  }, [tableData, copyTable]);

  const { headers, rows } = tableData;
  const totalRows = rows.length;
  const chartData = CHART_ENABLED ? detectChartData(headers, rows) : null;

  // Filter rows: include any row where at least one cell contains the search term
  const filteredRows = searchTerm.trim()
    ? rows.filter((row) =>
        row.some((cell) =>
          String(cell ?? '').toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : rows;

  return (
    <div className="msg-table-wrapper">
      {/* Export + copy buttons — top right, only shown when there is data */}
      {totalRows > 0 && (
        <div className="msg-table-export">
          {/* Chart toggle — only when data is chartable */}
          {chartData && (
            <>
              <button
                className={`msg-table-export-btn ${showChart ? 'msg-table-export-btn--active' : ''}`}
                onClick={() => setShowChart(v => !v)}
                title={showChart ? 'Show table' : 'Show chart'}
              >
                {showChart ? <TableIcon size={11} /> : <BarChart2 size={11} />}
                {showChart ? 'Table' : 'Chart'}
              </button>
              {showChart && (
                <button
                  className={`msg-table-export-btn ${chartType === 'line' ? 'msg-table-export-btn--active' : ''}`}
                  onClick={() => setChartType(t => t === 'bar' ? 'line' : 'bar')}
                  title="Toggle bar / line chart"
                >
                  {chartType === 'bar' ? 'Line' : 'Bar'}
                </button>
              )}
            </>
          )}
          {/* L1-7: Copy table as tab-separated text */}
          <button
            className={`msg-table-export-btn ${tableCopied ? 'msg-table-export-btn--done' : ''}`}
            onClick={copyTableAsText}
            title={tableCopied ? 'Copied!' : 'Copy table to clipboard (tab-separated)'}
          >
            {tableCopied ? <Check size={11} /> : <Copy size={11} />}
            {tableCopied ? 'Copied' : 'Copy'}
          </button>
          <button
            className="msg-table-export-btn"
            onClick={() => exportToCSV(headers, rows, sapModule)}
            title="Export to CSV"
          >
            <Download size={11} /> CSV
          </button>
          <button
            className="msg-table-export-btn"
            onClick={() => exportToExcel(headers, rows, sapModule)}
            title="Export to Excel"
          >
            <Download size={11} /> Excel
          </button>
        </div>
      )}

      {/* Search bar — only shown when there is data to search */}
      {totalRows > 0 && (
        <div className="msg-table-search">
          <Search size={13} className="msg-table-search-icon" />
          <input
            type="text"
            className="msg-table-search-input"
            placeholder={`Search ${totalRows} records…`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search table"
          />
          {/* Clear button — only shown when search is active */}
          {searchTerm && (
            <button
              className="msg-table-search-clear"
              onClick={() => setSearchTerm('')}
              aria-label="Clear search"
            >
              <X size={12} />
            </button>
          )}
        </div>
      )}

      {/* Chart panel */}
      {showChart && chartData && (
        <div className="msg-chart-panel">
          <ResponsiveContainer width="100%" height={220}>
            {chartType === 'bar' ? (
              <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--clr-border-soft)" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--clr-text-muted)' }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--clr-text-muted)' }} width={50} />
                <Tooltip contentStyle={{ background: 'var(--clr-bg-elevated)', border: '1px solid var(--clr-border)', borderRadius: 6, fontSize: 12 }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            ) : (
              <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--clr-border-soft)" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--clr-text-muted)' }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--clr-text-muted)' }} width={50} />
                <Tooltip contentStyle={{ background: 'var(--clr-bg-elevated)', border: '1px solid var(--clr-border)', borderRadius: 6, fontSize: 12 }} />
                <Line type="monotone" dataKey="value" stroke="var(--clr-accent)" strokeWidth={2} dot={{ fill: 'var(--clr-accent)', r: 3 }} />
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {/* Scrollable table area */}
      <div className="msg-table-scroll">
        <table className="response-table">
          <thead>
            <tr>
              {headers.map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.length > 0 ? (
              filteredRows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j}>{cell}</td>
                  ))}
                </tr>
              ))
            ) : (
              /* No match message */
              <tr>
                <td colSpan={headers.length} className="msg-table-no-results">
                  No records match "{searchTerm}"
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer: shows filtered count vs total */}
      <div className="msg-table-footer">
        {searchTerm
          ? `${filteredRows.length} of ${totalRows} records`
          : `${totalRows} record${totalRows !== 1 ? 's' : ''} returned from SAP`
        }
      </div>
    </div>
  );
}

// ─── Inline Text Formatter ────────────────────────────────────────────────────
// Supports: **bold**, *italic*, bullet lists starting with •

function FormattedText({ text }) {
  if (!text) return null;

  // Split into lines, render each
  const lines = text.split('\n');

  return (
    <div className="msg-text">
      {lines.map((line, i) => {
        if (!line.trim()) return <br key={i} />;

        // Bullet line
        if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
          return (
            <div key={i} className="msg-bullet">
              <span className="msg-bullet-dot">•</span>
              <span>{parseInline(line.trim().replace(/^[•\-]\s*/, ''))}</span>
            </div>
          );
        }

        return <p key={i}>{parseInline(line)}</p>;
      })}
    </div>
  );
}

/** Parses **bold** and *italic* within a single line */
function parseInline(text) {
  // Pattern: **bold** or *italic*
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);

  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
}

export default ChatMessage;
